/**
 * Why: The PII sub-spec defines three rules (1, 2, 3) that share the same
 * core question — "given the name of a field, should we redact its value?"
 * Keeping the deny-list + lookup logic in one place means the allow-list
 * can be audited in exactly one file on the JS side, mirroring the PHP
 * KeyRedactor service.
 * Scope: New field patterns land in the constants here; callers remain
 * unchanged. Callers choose which strategy to apply (strip vs redact)
 * based on where in the payload the field lives.
 * SRP: Decide whether a single field name — header, body key, or query
 * param — is sensitive, and perform the appropriate redaction.
 *
 * See `~/.claude-specs/incentco/ICOV3/ICOV3-1386-pii-scrubbing.md`
 * Rules 1, 2, 3. Mirrors `app/Sentry/KeyRedactor.php` in the API repo.
 */

export const REPLACEMENT = '[REDACTED]';

const STRIP_HEADERS = [
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-csrf-token',
  'x-xsrf-token',
  'x-api-key',
  'x-auth-token',
  'x-twilio-auth-token',
];

const ALLOW_KEYS = [
  'user_id', 'organization_id', 'organization_ids',
  'program_id', 'program_ids', 'merchant_id', 'redeemed_merchant_id',
  'redeemed_program_id', 'event_id', 'request_id',
];

const REDACT_SUBSTRINGS = [
  'password', 'secret',
  'card_number', 'credit_card', 'cc_number',
  'cvv', 'cvc', 'card_code', 'security_code',
  'account_number', 'routing_number', 'bank_account',
  'ssn', 'social_security',
  'dob', 'date_of_birth', 'birthdate',
  'driver_license', 'drivers_license', 'license_number',
  'email', 'phone', 'mobile',
  'first_name', 'last_name', 'full_name',
  'address', 'street', 'line_1', 'line_2', 'zip', 'postal_code',
  'twilio_auth_token', 'twilio_account_sid', 'sendgrid_api_key',
];

const REDACT_EXACT = [
  'token', 'auth_token', 'access_token', 'refresh_token',
  'api_token', 'reset_token', 'invite_token',
  'api_key', 'secret_key', 'private_key',
  'cell',
];

const REDACT_QUERY_KEYS = [
  'token', 'access_token', 'api_key', 'auth',
  'secret', 'email', 'password', 'reset_token', 'invite_token', 'code',
];

/**
 * Why: Authorization/Cookie/CSRF headers carry raw credentials. Stripping
 * them is strictly safer than redacting — even the value shape could
 * hint at a token format, and we don't need the header present at all
 * for debugging value.
 * Scope: New denied headers go in `STRIP_HEADERS`. Match is case-insensitive.
 * SRP: Remove every denied header from a headers object.
 *
 * @param {Object} headers Raw header map from Sentry's request data
 * @returns {Object}       Same shape, denied entries absent
 */
export function stripHeaders(headers) {
  if (!headers || typeof headers !== 'object') {
    return headers;
  }
  const result = {};
  Object.keys(headers).forEach((key) => {
    if (!STRIP_HEADERS.includes(key.toLowerCase())) {
      result[key] = headers[key];
    }
  });
  return result;
}

/**
 * Why: Redact-key matching needs to be precise — we want to redact
 * `password` and `reset_password_token` but NOT `last_password_change`
 * if that were meaningful. Allow-list wins over everything, then
 * exact-match (for token variants that would over-match as substrings),
 * then substring match.
 * Scope: Adjusting ordering or introducing a regex layer happens here.
 * SRP: Classify a single field name as "redact or keep".
 *
 * @param {string} key The raw key name
 * @returns {boolean}  True if the value should be replaced with the placeholder
 */
export function shouldRedactKey(key) {
  const lower = String(key).toLowerCase();
  if (ALLOW_KEYS.includes(lower)) {
    return false;
  }
  if (REDACT_EXACT.includes(lower)) {
    return true;
  }
  return REDACT_SUBSTRINGS.some((needle) => lower.includes(needle));
}

/**
 * Why: Form/JSON body data can contain arbitrarily nested structures. A
 * shallow redaction would miss PII buried in nested payloads. Recursive
 * walking ensures every key at every depth is checked against the
 * allow-list.
 * Scope: Deep-merge or structure-aware rules would extend here without
 * touching callers.
 * SRP: Walk a nested object/array, replacing values for any key matching
 * the redact rules with the placeholder string.
 *
 * @param {*} data Any JSON-like value (object, array, primitive)
 * @returns {*}    Same shape with sensitive values replaced
 */
export function redactFields(data) {
  if (Array.isArray(data)) {
    return data.map((item) => redactFields(item));
  }
  if (data === null || typeof data !== 'object') {
    return data;
  }
  const result = {};
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (typeof value === 'object' && value !== null) {
      result[key] = redactFields(value);
      return;
    }
    result[key] = shouldRedactKey(key) ? REPLACEMENT : value;
  });
  return result;
}

/**
 * Why: Query strings ship as `?token=xxx&email=x@y&page=2`. Sensitive
 * params need redaction while legitimate pagination/filter params should
 * survive so dashboard readers can see what endpoint was hit.
 * Scope: If we need per-route query scrubbing later, this function gets
 * the extra context; constants stay central.
 * SRP: Parse a URL query string, redact denied params, rebuild.
 *
 * @param {string} queryString Raw query string (no leading `?`)
 * @returns {string}           Rebuilt query string with denied params replaced
 */
export function redactQueryString(queryString) {
  if (typeof queryString !== 'string' || queryString === '') {
    return queryString;
  }
  const params = new URLSearchParams(queryString);
  const rebuilt = new URLSearchParams();
  params.forEach((value, key) => {
    if (REDACT_QUERY_KEYS.includes(key.toLowerCase())) {
      rebuilt.append(key, REPLACEMENT);
      return;
    }
    rebuilt.append(key, value);
  });
  return rebuilt.toString();
}
